import { defineStore } from 'pinia'
import type {
  ResQueryAdminDistricts,
  ArgQueryAdminDistricts
} from '@/types/accessor/api/adminDistricts/adminDistricts'
import { accessDataBasedOnNetwork } from '@/accessors/common/dataAccessorBasedOnNetwork'
import { useNetworkStore } from './networkStore'
import { GlobalError } from '@/utils/globalError/GlobalError'
import { ERROR_CODES } from '@/constants/error/errorCode'

export type Option = Readonly<{
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any
  text: string
}>

interface State {
  street: ResQueryAdminDistricts
  block: ResQueryAdminDistricts
}

export const useAdminDistrictsStore = defineStore('adminDistrictsStore', {
  state: (): State => ({
    street: {
      result: [],
      lastEvaluatedKey: undefined
    },
    block: {
      result: [],
      lastEvaluatedKey: undefined
    }
  }),

  getters: {
    getStreetResult: state => state.street.result,
    getBlockResult: state => state.block.result,
    getStreetList: state => {
      const streetList: Array<Option | undefined> = state.street.result
        .map(e => {
          if (e.streetName !== '' && e.streetCode !== '') {
            return { value: e.streetCode, text: e.streetName }
          }
          return undefined
        })
        .filter(s => s !== undefined)

      streetList.sort((a, b) => {
        return a?.text !== undefined && b?.text !== undefined && a.text < b.text ? -1 : 1
      })

      const orderedStreetList: Option[] = streetList.reduce<Option[]>((prev, cur) => {
        if (!prev.some(street => street.text === cur?.text)) {
          if (cur !== undefined) {
            prev.push(cur)
          }
        }
        return prev
      }, [])

      return orderedStreetList
    },
    getBlockList: state => (streetCode: string) => {
      const blocks = state.block.result
        .filter(b => b.streetCode === streetCode)
        .map(b => b.blockNumber)

      blocks.sort((a, b) => {
        return Number(a) < Number(b) ? -1 : 1
      })

      const uniqueBlocks = Array.from(new Set(blocks))
      return uniqueBlocks
    }
  },

  actions: {
    updateStreetState(obj: ResQueryAdminDistricts) {
      this.street.result = obj.result
      this.street.lastEvaluatedKey = obj.lastEvaluatedKey
    },
    updateBlockState(obj: ResQueryAdminDistricts) {
      this.block.result = obj.result
      this.block.lastEvaluatedKey = obj.lastEvaluatedKey
    },
    /**
     * StoreのStateに保持しているデータを初期化する
     */
    initializeState() {
      this.block.result = []
      this.block.lastEvaluatedKey = undefined
      this.street.result = []
      this.street.lastEvaluatedKey = undefined
    },
    async getStreetListAction(params: {
      govCode: string | undefined
      lastEvaluatedKey?: object
      viewCode?: string
    }) {
      if (params === undefined || params.govCode === undefined || params.govCode === '') return

      const networkStore = useNetworkStore()

      if (this.street.result.length === 0) {
        const adminDistrictsQueryParams: ArgQueryAdminDistricts = {
          govCode: params.govCode,
          type: 'street',
          exclusiveStartKey:
            params.lastEvaluatedKey === undefined ? undefined : params.lastEvaluatedKey
        }

        try {
          const adminDistrictsItem = await accessDataBasedOnNetwork(
            'queryAdminDistrictsDaoApi',
            adminDistrictsQueryParams,
            networkStore.getIsOnline,
            () => {
              networkStore.setOfflineStatus()
            }
          )

          if (!adminDistrictsItem) return

          this.updateStreetState(adminDistrictsItem)

          if (adminDistrictsItem.lastEvaluatedKey) {
            const nextParam = {
              govCode: params.govCode,
              lastEvaluatedKey: adminDistrictsItem.lastEvaluatedKey
            }
            await this.getStreetListAction(nextParam)
          }
        } catch (err) {
          console.error(err)
          throw new GlobalError([ERROR_CODES.HOME_ADMIN_DISTRICTS_GET_FAILED])
        }
      }
    }
  }
})
